import React from "react";
import { useTranslation } from "react-i18next";
import Box from "Components/Atoms/Box";
import Typography from "Components/Atoms/Typography";
import SwitchBox from "Components/Atoms/Switch";
import Select from "Components/Atoms/Select";
import { ReservationLength, ReservationShift } from "types/shifts";
import { blockToTime } from "utils/helper";

type Props = {
  shift: Partial<ReservationShift>;
  onChange: (length: ReservationLength) => void;
};

const ShiftStayUntilSettings = ({ shift, onChange }: Props) => {
  const { t } = useTranslation("calendar");
  const length: ReservationLength = shift.additional?.reservationLength ?? {
    standard: 8,
    rules: [],
  };
  const close = shift.close;
  if (
    typeof close !== "number" ||
    shift.closed ||
    shift.commentOnly ||
    shift.allDay ||
    length.avoidDoubleSeating
  )
    return null;

  const service = shift.service ?? close - 1;
  const enabled =
    Number.isInteger(length.stayUntil) &&
    (length.stayUntil ?? 0) <= 112 &&
    (length.stayUntil ?? 0) > close;
  const end = enabled ? length.stayUntil! : close;
  const requiredEnd = service + length.standard;
  const shortensReservations = end < requiredEnd;
  if (!enabled && !shortensReservations) return null;
  const options = Array.from({ length: 113 }, (_, id) => ({
    id,
    label: blockToTime(id),
  })).filter((option) => option.id > close);

  return (
    <Box className="mg-bt-md">
      {shortensReservations && (
        <Typography
          role="alert"
          variant="text-3"
          color="error"
          block
          translation={null}
          className="mg-bt-sm"
        >
          {t(
            "The latest end of stay shortens reservations. To offer the standard duration at the last arrival, allow stays until at least {{time}}. A following shift or a minimum duration does not extend this limit.",
            { time: blockToTime(requiredEnd) }
          )}
        </Typography>
      )}
      <SwitchBox
        label={t("Allow stays beyond the shift end")}
        checked={enabled}
        disabled={!options.length}
        color="primary"
        onChange={(_, checked) => {
          const next = { ...length };
          if (checked)
            next.stayUntil = Math.min(112, Math.max(close + 1, requiredEnd));
          else delete next.stayUntil;
          onChange(next);
        }}
      />
      {enabled && (
        <Select
          label={t("Latest end of stay")}
          margins={0}
          inputProps={{ "aria-label": t("Latest end of stay") }}
          value={length.stayUntil}
          options={options}
          onChange={(event) =>
            onChange({ ...length, stayUntil: Number(event.target.value) })
          }
        />
      )}
    </Box>
  );
};

export default ShiftStayUntilSettings;
